import {Button, Icon, IconButton} from '@wandb/components';
import {TextField} from '@wandb/weave/components/Form/TextField';
import React, {useCallback, useState} from 'react';

import {useWBAgentController} from '../../../agentChat/controller';
import {useAgentChatReadonly} from '../../../agentChat/useAgentChatReadonly';
import * as FilterActions from '../../../state/views/filter/actions';
import {type Ref} from '../../../state/views/filter/types';
import * as ViewHooks from '../../../state/views/hooks';
import {useViewAction} from '../../../state/views/hooks';
import type * as FilterTypes from '../../../util/filterTypes';
import {type StoredFilter} from '../../../util/filterV2';
import {type FilterKeySelectorCreatorProps} from '../../Filters/FilterKeySelector';
import {entryPointPrompts} from '../../WBAgent/suggestions/catalog';
import {WBTableActionFilterPicker} from '../WBTable/WBTableActionsFilter';
import {type RunFilterValueSelectorCreatorProps} from './RunsFilterValueSelector';

interface RunsFilterPickerProps {
  entityName: string;
  projectName: string;
  filtersRef: Ref;

  defaultToggleFilters?: FilterTypes.DefaultToggleFilter[];
  hideManuallyHidden?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
  };
  onAgentChatOpened(): void;
  onFiltersChanged(): void;

  filterKeySelector(props: FilterKeySelectorCreatorProps): React.ReactNode;
  filterValueSelector(
    props: RunFilterValueSelectorCreatorProps
  ): React.ReactNode;
}

export const RunsFilterPicker = (props: RunsFilterPickerProps) => {
  const {
    filtersRef,
    onAgentChatOpened,
    onFiltersChanged,
    defaultToggleFilters,
    hideManuallyHidden,
  } = props;
  const storedFilters = ViewHooks.usePart(filtersRef);
  const setFilters = useViewAction(filtersRef, FilterActions.set);
  const {isAvailable: isAgentChatAvailable, startChat} = useWBAgentController();
  const isAgentChatReadonly = useAgentChatReadonly();
  const [agentPrompt, setAgentPrompt] = useState('');

  const setFiltersWrapped = useCallback(
    (f: StoredFilter) => {
      setFilters(f);
      onFiltersChanged();
    },
    [setFilters, onFiltersChanged]
  );

  const openAgentChat = useCallback(() => {
    const request = agentPrompt.trim();
    if (request === '') {
      return;
    }

    const result = startChat(entryPointPrompts.filterRuns(request), {
      submit: true,
    });
    if (result.ok) {
      setAgentPrompt('');
      onAgentChatOpened();
    }
  }, [agentPrompt, onAgentChatOpened, startChat]);

  return (
    <WBTableActionFilterPicker
      filters={storedFilters}
      defaultToggleFilters={defaultToggleFilters}
      hideManuallyHidden={hideManuallyHidden}
      setFilters={setFiltersWrapped}
      filterKeySelector={props.filterKeySelector}
      filterValueSelector={props.filterValueSelector}
      headerContent={
        isAgentChatAvailable ? (
          <div
            className="relative mb-16 [&_input]:!pl-36 [&_input]:!pr-128"
            data-dd-action-name="Filter runs with agent">
            <span className="pointer-events-none absolute inset-y-0 left-8 z-10 flex items-center">
              <Icon icon="lightboard-solid" color="moon" size="sm" />
            </span>
            <TextField
              ariaLabel="Describe run filters to ARIA"
              disabled={isAgentChatReadonly}
              placeholder="Describe the filters you want ARIA to create"
              size="large"
              value={agentPrompt}
              onChange={setAgentPrompt}
              onKeyDown={(key, event) => {
                // Ignore IME composition events.
                if (event.nativeEvent.isComposing) {
                  return;
                }
                if (key === 'Enter') {
                  event.preventDefault();
                  openAgentChat();
                }
              }}
            />
            {agentPrompt.trim() !== '' && (
              <div className="absolute inset-y-0 right-8 z-10 flex items-center gap-4">
                <Button
                  endIcon="enter-return"
                  isDisabled={isAgentChatReadonly}
                  size="sm"
                  variant="ghost"
                  onPress={openAgentChat}>
                  Create
                </Button>
                <IconButton
                  aria-label="Clear filter request"
                  icon="close"
                  size="sm"
                  variant="ghost"
                  onPress={() => setAgentPrompt('')}
                />
              </div>
            )}
          </div>
        ) : null
      }
    />
  );
};
